import api from './api';

/**
 * Lien transport ↔ station (lot 032) : stations livrables, stations à
 * réapprovisionner, réceptions à faire, voyage vers ma station.
 *
 * Chemins et formes de réponse : `backend/app/api/v1/station_supply.py`.
 * La préparation des réceptions d'un voyage porte une clé `Idempotency-Key` :
 * un renvoi après coupure rend les mêmes réceptions, sans doublon. Les
 * chiffres (écarts, prix de revient, marge) viennent du serveur ; montants
 * absents (`null`) pour un profil qui ne voit pas les montants.
 */

const unwrap = (response) => response?.data?.data ?? response?.data;

const nouvelleCle = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

/** Codes de la nature d'une commande (`libelle('commande_nature', code)`). */
export const NATURE_VENTE = 'SALE';
export const NATURE_REAPPRO = 'INTERNAL_SUPPLY';

/** Modes d'une station exploitée par la société (réapprovisionnement interne). */
export const MODES_PROPRES = ['OWNED', 'MANAGED'];

export const estReappro = (commande) => String(commande?.supply_kind || '').toUpperCase() === NATURE_REAPPRO;

export const supplyApi = {
  /** Stations livrables par une commande (les nôtres et les revendeurs), avec leurs cuves actives. */
  async stations() {
    return unwrap(await api.get('/station-supply/stations')) || [];
  },

  /** Nos stations, la plus pressée d'abord : autonomie, commandes en cours, réceptions attendues. */
  async resupply() {
    return unwrap(await api.get('/station-supply/resupply')) || [];
  },

  /**
   * Cuves à réapprovisionner (autonomie sous 2 jours, litres jusqu'à 90 % de la capacité)
   * et regroupement par ville : `{ proposals, groups, total_liters }` (lot 035).
   */
  async replenishment() {
    return unwrap(await api.get('/station-supply/replenishment')) || { proposals: [], groups: [], total_liters: 0 };
  },

  /**
   * Pleins d'un camion de la société à la pompe de nos stations : `{ refuels, summary, total }` (lot 035).
   * `skip` saute les pleins déjà lus (les plus récents d'abord); le résumé porte sur tous les pleins.
   */
  async truckRefuels(truckId, { limit = 50, skip = 0 } = {}) {
    return (
      unwrap(await api.get(`/station-supply/trucks/${truckId}/refuels`, { params: skip ? { limit, skip } : { limit } })) || {
        refuels: [],
        summary: null,
      }
    );
  },

  /** Réceptions attendues ou en cours dans nos stations. */
  async receptions({ limit = 100 } = {}) {
    return unwrap(await api.get('/station-supply/receptions', { params: { limit } })) || [];
  },

  /** Une réception attendue par cuve que livre le voyage, pré-remplie. */
  async prepare(tripId, cle) {
    return (
      unwrap(
        await api.post(`/station-supply/trips/${tripId}/receptions`, {}, {
          headers: { 'Idempotency-Key': cle || nouvelleCle() },
        }),
      ) || []
    );
  },

  /** Chaîne chargé → arrivé → reçu → vendu, écarts, prix de revient, résultat. */
  async chain(tripId) {
    return unwrap(await api.get(`/station-supply/trips/${tripId}/chain`));
  },
};

export default supplyApi;
